import Vue from 'vue';
import mqtt from "mqtt";
import axios from 'axios'
import axiosRetry from 'axios-retry'
import {TracePond} from "@etyoul/tracepond";

axiosRetry(axios, {
	retries: 3,
	retryDelay: axiosRetry.exponentialDelay,
	retryCondition: (error) => {
		// 5xx 서버 오류나 네트워크 오류 시 재시도
		return axiosRetry.isNetworkOrIdempotentRequestError(error) ||
			   (error.response && error.response.status >= 500) ||
			   (error.response && error.response.status === 401)
	},
})

let clientPrefix
// 재연결은 presign(HTTP) 호출을 동반하므로 고정 간격 재시도는 백엔드·허브 람다 폭주가 된다 — 지수 백오프
const MQTT_RETRY_BASE_MS = 1000
const MQTT_RETRY_MAX_MS = 60000
let mqttRetryDelay = MQTT_RETRY_BASE_MS
let mqttReconnectTimer = null
// presigned URL 만료(기본 3000초) 5분 전 선제 재연결 — 세대별 clientId(겹침 재연결)로 will 오발화를 막는다
let mqttExpiryTimer = null
let mqttGeneration = 0
let mqttConnecting = false
let wakeWatcherStarted = false
// 현재 연결의 clientId — 지시서 열람 주체를 "사용자"가 아니라 "연결" 단위로 식별하기 위해 발행에 싣는다.
// 같은 계정이 여러 탭·기기로 접속하면 한 연결이 끊겨도 다른 연결의 열람은 유지되어야 한다.
let currentClientId = ''
// 현재 연결 세대 — 발행·LWT 에 함께 실어 지연 LWT 를 구분한다
let currentGeneration = 0
// 이 연결이 "열어둔" 지시서 목록.
//   로그아웃(`$disconnect()` → `client.end()`)은 MQTT 정상 종료라 **LWT 가 발화하지 않는다**.
//   그러면 열어둔 지시서가 닫히지 않아 engine 캐시·DB 에 "진행중" 으로 남고,
//   그 프로젝트는 아무도(본인조차) 다시 열 수 없다 — 2026-08-20 교육에서 실제로 발생했다.
//   따라서 끊기 전에 여기 담긴 항목을 직접 닫는다.
let openDirections = []
// 열림/닫힘 판정은 engine 의 `GameHandler.setDirectionStatus` 와 **같은 기준**이어야 한다.
// 양쪽이 어긋나면 한쪽에만 항목이 남아 유령 열람이 된다.
const isDirectionOpen = (part, data) => part === 'make'
	? (data.make_status === 1 && !data.inspected)
	: [1, 3].indexOf(data[`${part}_status`]) > -1
const directionKey = data => `${data.part}/${data.type}/${data.order}`
// 탭(페이지 로드) 식별자. 모듈 스코프이므로 로드마다 새로 만들어지고 재연결 중에는 유지된다.
//   ⚠️ sessionStorage 에서 읽어 재사용하면 안 된다 — 탭 복제·링크로 새 탭을 열면 sessionStorage 가
//   복사되어 두 탭이 같은 clientId 를 갖게 되고, IoT Core 가 뒤 연결로 앞 연결을 강제 종료(takeover)한다.
//   그때 발화한 LWT 가 남의 열람까지 닫아 "둘 다 같은 지시서를 연" 상태가 만들어진다.
//   sessionStorage 에는 MakeInspection 의 자기 메시지 필터링 호환을 위해 "쓰기만" 한다.
let tabPostfix = ''
// 반응형 시계 기동 플래그(전역 1개)
let clockStarted = false
const CLOCK_TICK_MS = 5000
// MQTT 연결 실패가 무한 재시도되는 동안 tracepond 로그 폭주를 막기 위한 쿨다운(ms)
// event별로 독립 적용 — 같은 원인의 폭주만 누르고, 다른 계열 오류는 즉시 통과
const MQTT_ERROR_REPORT_COOLDOWN = 60 * 1000
let lastMqttErrorReportedAt = {}
// QoS 1 중복 수신 방어(msgId 캐시)
const MQTT_DEDUP_MAX = 500
const seenMsgIds = new Set()
const seenMsgIdQueue = []
// insecure context(http dev)에선 crypto.randomUUID가 없다 — 폴백 포함
const newMsgId = () => (window.crypto && window.crypto.randomUUID)
	? window.crypto.randomUUID()
	: Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10)
const isDuplicateMessage = message => {
	try {
		const parsed = JSON.parse(message)
		const id = parsed && parsed.msgId
		if (!id) return false
		if (seenMsgIds.has(id)) return true
		seenMsgIds.add(id)
		seenMsgIdQueue.push(id)
		if (seenMsgIdQueue.length > MQTT_DEDUP_MAX) {
			seenMsgIds.delete(seenMsgIdQueue.shift())
		}
		return false
	} catch (e) {
		return false
	}
}

export default {
	data() {
		return {
			// 오류 보고용 — presign 이후 IoT endpoint 호스트가 담긴다
			host: '',
		}
	},

	created() {
		// 반응형 시계(전역 1개) — round/gameStatus 가 moment() 를 직접 읽으면 시간이 흘러도
		// 재계산되지 않아, 라운드가 끝나도 "진행 중" 문구가 남는다(2026-08-21 관리자 화면).
		if (!clockStarted) {
			clockStarted = true
			this.$gameData.now = moment().format('YYYY-MM-DD HH:mm:ss')
			setInterval(() => {
				this.$gameData.now = moment().format('YYYY-MM-DD HH:mm:ss')
			}, CLOCK_TICK_MS)
		}
	},

	computed: {
		client: {
			get() { return this.$gameData.client },
			set(client) { this.$gameData.client = client }
		},
		user: {
			get() { return this.$userData.user },
			set(user) { this.$userData.user = user }
		},
		setting: {
			get() { return this.$userData.setting },
			set(setting) { this.$userData.setting = setting }
		},
		deliveries: {
			get() { return this.$userData.deliveries },
			set(deliveries) { this.$userData.deliveries = deliveries }
		},
		trainingData: {
			get() { return this.$userData.data},
			set(trainingData) { this.$userData.data = trainingData }
		},
		teamData: {
			get() { return this.$userData.teamData },
			set(teamData) { this.$userData.teamData = teamData }
		},
		stocks: {
			get() { return this.$userData.stocks },
			set(stocks) { this.$userData.stocks = stocks }
		},
		inventories: {
			get() { return this.$userData.inventories },
			set(inventories) { this.$userData.inventories = inventories }
		},

		training: {
			get() { return this.$adminData.training },
			set(training) { this.$adminData.training = training }
		},
		mqttData() {
			return this.$gameData.mqttData
		},
		round() {

			// $gameData.now(반응형)를 읽어야 시간 경과로도 재계산된다 — 초기 틱 전에는 직접 계산
			const now = this.$gameData.now || moment().format('YYYY-MM-DD HH:mm:ss')
			const training = this.$adminData.training
			if (!training.id) return 0

			const start1 = `${training.date} ${training.start_time1}`
			const end1 = `${training.date} ${training.end_time1}`
			const start2 = `${training.date} ${training.start_time2}`
			const end2 = `${training.date} ${training.end_time2}`

			if (now < start1) {
				return -1
			} else if (now >= start1 && now <= end1)  {
				return 1
			} else if (now < start2)  {
				return 1.5
			} else if (now >= start2 && now <= end2)  {
				return 2
			}

			return 3

		},
		gameStatus() {

			let status = ''

			const round = this.round
			if (!round) return ''

			if (round === -1) {
				status = '예약'
			} else if (round === 1) {
				status = '1라운드 진행 중'
			} else if (round === 1.5) {
				status = '2라운드 준비'
			} else if (round === 2) {
				status = '2라운드 진행 중'
			} else if (round === 3) {
				status = '교육 종료'
			}

			return status
		},
	},

	watch: {
	},

	methods: {

		/**
		 * CALL API
		 */
		async $send(method, path, data) {

			// let result
			// path = process.env.VUE_APP_API_URL + path
			// const res = await Vue.axios[method.toLowerCase()](path, data)
			// result = res.data
			//
			// return result


			let res
			try {
				let token
				if (this.$route.path.startsWith('/manage')) {
					token = this.$cookie.get('tpc_token')
				} else if (this.$route.path.startsWith('/admin') || this.$route.path === '/status') {
					token = this.$cookie.get('admin_token')
				} else {
					token = this.$cookie.get('token')
				}

				// const token = localStorage.getItem('token')

				data = {...data, token: token}

				if (method === 'get') {
					Object.keys(data).forEach(k => {
						if (path.indexOf('?') > -1) {
							path += `&${k}=` + data[k]
						} else {
							path += `?${k}=` + data[k]
						}
					})
				}

				let result
				path = process.env.VUE_APP_API_URL + path
				// const res = await Vue.axios[method.toLowerCase()](path, data)
				res = await axios({
					method: method.toLowerCase(),
					url: path,
					data,
					headers: {
						'Content-Type': 'application/json',
					},
				})
				if (res.data.code === '9001') {
					// 끊기 전에 열어둔 지시서를 닫는다(정상 종료라 LWT 가 대신 닫아주지 않는다)
					await this.$closeOpenDirections()
					this.$disconnect()
					this.$cookie.delete('token')
					location.replace('/login')
					return
				}
				result = res.data

				return result
			} catch (e) {
				console.error(e)

				TracePond.sendErrorToServer(e, {
					source: {
						url: path,
						data,
						res,
						user: this.user
					}
				})
				return {}
			}

		},
		async $get(path, data) {
			return await this.$send('get', path, data)
		},
		async $post(path, data) {
			if (this.$route.path !== '/login') {
				if (this.$route.path.startsWith('/manage')) {
					if (!this.$cookie.get('tpc_token')) {
						location.replace('/login')
						return
					}
				} else if (this.$route.path.startsWith('/admin') || this.$route.path === '/status') {
					if (!this.$cookie.get('admin_token')) {
						location.replace('/login')
						return
					}
				} else {
					if (!this.$cookie.get('token')) {
						location.replace('/login')
						return
					}
				}
			}
			return await this.$send('post', path, data)
		},

		async $wait(milliseconds) {
			await new Promise(resolve => setTimeout(resolve, milliseconds));
		},

		async $init() {
		},

		nl2br(message) {
			return (message || '').trim().replace(/\n/g, '<br />')
		},

		async $reportError(err) {
			console.error(err)
			// await this.$post('/report/error', {
			// 	id: this.$cookie.get('id'),
			// 	error: err.stack.toString()
			// })
		},
		reconnectMqtt() {
			// 타이머 가드: error·close가 연달아 와도 재연결은 한 번만 예약된다
			if (mqttReconnectTimer) return
			const delay = mqttRetryDelay
			mqttRetryDelay = Math.min(mqttRetryDelay * 2, MQTT_RETRY_MAX_MS)
			mqttReconnectTimer = setTimeout(() => {
				mqttReconnectTimer = null
				this.$setMqttClient()
			}, delay)
		},
		$reportMqttError(err, event) {
			// 연결이 계속 실패하면 500ms 간격으로 재시도되므로, 같은 event의 쿨다운 내 중복 전송은 건너뛴다
			const now = new Date().getTime()
			if (now - (lastMqttErrorReportedAt[event] || 0) < MQTT_ERROR_REPORT_COOLDOWN) return
			lastMqttErrorReportedAt[event] = now
			TracePond.sendErrorToServer(err instanceof Error ? err : new Error('MQTT ' + event + ': ' + err), {
				source: {
					type: 'mqtt',
					event,
					host: this.host,
					user: this.user
				}
			})
		},
		async $setMqttClient() {
			// presign(HTTP 호출)을 동반하므로 동시 재진입을 막는다
			if (mqttConnecting) return
			mqttConnecting = true

			try {
				// 세대별 clientId — 재-presign 재연결 시 같은 clientId로 붙으면 takeover로
				// 구 연결의 will이 발화해 열어둔 지시서가 강제로 닫힌다. 세대를 붙여 겹침 재연결한다.
				let base = 'legorace-'
				if (this.user && this.user.id) {
					base += this.user.id
				} else {
					base += 'admin-' + this.$adminData.admin.id

					if (this.$route.path === '/status') {
						base += '-status'
					}
				}
				if (!tabPostfix) {
					tabPostfix = Math.random().toString(36).slice(2, 10)
					// MakeInspection 이 자기 발행분을 걸러내는 데 쓰므로 값은 남겨둔다(읽지는 않는다)
					sessionStorage.setItem('cid_post', tabPostfix)
				}
				mqttGeneration += 1
				const clientId = `${base}-${tabPostfix}-${mqttGeneration}`
				// 열람 주체는 "이 화면(기기·탭)" 단위다 — 세대를 빼야 재연결해도 동일 주체로 인식된다.
				// 세대는 별도 필드(gen)로 실어 engine 이 "구 연결의 지연 LWT 가 새로 연 항목을
				// 닫아버리는" 경우를 걸러낸다(자기 세대 이하만 닫는다).
				currentClientId = `${base}-${tabPostfix}`
				currentGeneration = mqttGeneration
				console.log('clientId', clientId, '/ owner', currentClientId, '/ gen', currentGeneration)

				const res = await this.$get('/mqtt/presign', { clientId })
				if (!res || res.code !== '0000' || !res.data || !res.data.url) {
					console.error('mqtt presign 실패 code =', res && res.code, ', message =', res && res.message)
					this.$reportMqttError(new Error('presign failed: ' + (res && res.code)), 'presign.error')
					mqttConnecting = false
					this.reconnectMqtt()
					return
				}
				const { url, topics, expiresInSec } = res.data
				clientPrefix = res.data.topicPrefix + '/'
				try { this.host = new URL(url).host } catch (e) { this.host = '' }

				// 화면 복귀 감지 — 복귀 시 reload로 REST 재조회(재호출돼도 1회만 등록).
				//   ⚠️ 과거에는 setInterval 이 오래 멈췄는지로 절전을 "추정"했는데, 모바일 크롬은
				//   화면이 꺼지면 페이지를 freeze 했다가 복귀 시 타이머 스케줄을 리셋해 갭이 잡히지 않는
				//   경우가 있다. 그러면 소켓은 끊겨 LWT 로 지시서가 닫혔는데 화면만 열린 채 남는다.
				//   visibilitychange 는 브라우저가 복귀 시 확실히 발생시키므로 이를 기준으로 한다.
				if (!wakeWatcherStarted) {
					wakeWatcherStarted = true
					let hiddenAt = 0
					let reloading = false
					const reloadOnce = reason => {
						// pageshow 와 visibilitychange 가 함께 오는 브라우저가 있어 중복 호출을 막는다
						if (reloading) return
						reloading = true
						console.log('wake up — ' + reason + ', reload')
						location.reload()
					}
					document.addEventListener('visibilitychange', () => {
						if (document.hidden) {
							hiddenAt = new Date().getTime()
							return
						}
						if (!hiddenAt) return   // 숨겨진 적 없이 발생한 이벤트는 무시(최초 로드 직후 reload 루프 방지)
						const away = new Date().getTime() - hiddenAt
						hiddenAt = 0
						// 전환 길이와 무관하게 항상 새로고침한다.
						//   짧은 전환은 재조회 신호만 줘서 입력을 지키려 했으나, 지시서를 열고 있었다면
						//   pagehide 가 전환 즉시 닫아버려(각 탭의 unLoadEvent) 지킬 입력이 애초에 없다.
						//   반대로 재조회만 하면 freeze 중 죽은 MQTT 연결을 되살리지 못해, close 감지까지
						//   최대 45초(keepalive 30초 × 1.5) 동안 실시간 갱신이 끊긴 채 화면만 정상으로 보인다.
						reloadOnce('away ' + away + 'ms')
					})
					// BFCache 복원은 visibilitychange 없이 pageshow 만 오는 경우가 있다. 이때 페이지는
					// freeze 직전 상태 그대로 되살아나므로(열어둔 다이얼로그 포함) 반드시 새로고침한다.
					window.addEventListener('pageshow', event => {
						if (event.persisted) reloadOnce('bfcache restore')
					})
				}

				let will = null
				if (this.user.id && topics.will) {
					will = {
						topic: topics.will,
						// clientId 는 열람 주체(세대 제외)를 보낸다 — engine 이 store 의 소유자와 비교하므로
						// 연결 세대가 섞이면 재연결 뒤 끊길 때 대상을 찾지 못한다.
						payload: JSON.stringify({ user_id: this.user.id, clientId: currentClientId, gen: currentGeneration, agent: window? window.navigator.userAgent : '' }),
						qos: 1,
					}
				}

				const prev = this.client
				const firstConnection = !prev
				let settled = false

				// 세션 미유지(clean:true) — 재접속 복구는 오프라인 큐 재생이 아니라 REST refetch.
				// 옛 상태 메시지가 refetch 직후 도착해 최신 상태를 되돌리는 문제를 원천 차단한다.
				const next = mqtt.connect(url, {
					clientId,
					clean: true,
					keepalive: 30,
					reconnectPeriod: 0,
					will: will,
				})

				next.on('connect', async t => {
					console.log('connected ' + next.connected + ' (gen=' + mqttGeneration + ')')
					settled = true
					mqttConnecting = false
					mqttRetryDelay = MQTT_RETRY_BASE_MS

					// 구독 재확립을 먼저, refetch는 그 다음 — 순서가 바뀌면 그 사이 메시지가 샌다
					;(topics.subscribe || []).forEach(t2 => next.subscribe(t2, { qos: 1 }))
					console.log('subscribed to topics', topics.subscribe)

					this.client = next
					window.client = next

					// 새 연결 구독 완료 후 구 연결 정상 종료(DISCONNECT — will 미발행)
					if (prev && prev !== next) {
						try { prev.end() } catch (e) { console.error('구 MQTT 연결 정리 실패', e) }
					}

					// presigned URL 만료 5분 전 선제 재연결
					if (mqttExpiryTimer) clearTimeout(mqttExpiryTimer)
					mqttExpiryTimer = setTimeout(() => {
						console.log('presigned URL 만료 임박 — 선제 재연결')
						this.$setMqttClient()
					}, Math.max(((expiresInSec || 3000) - 300), 60) * 1000)

					// 재연결이면 공백 구간 복구: 상태 원천(DB)을 REST로 재조회하고 화면에 신호를 준다
					if (!firstConnection) {
						if (this.user.id) {
							await this.$getUserData()
						}
						this.$gameData.mqttData = { target: 'system', action: 'reconnected' }
						setTimeout(() => { this.$gameData.mqttData = null }, 10)
					}
				})
				next.on('error', err => {
					console.error('connect error ' + err)
					this.$reportMqttError(err, 'client.error')
					if (!settled) {
						mqttConnecting = false
						try { next.end(true) } catch (e) { console.error('연결 실패 정리 오류', e) }
						this.reconnectMqtt()
					}
					// 연결 후 오류는 close 이벤트가 이어져 거기서 재연결한다
				})
				next.stream.on('error', err => {
					console.error('connect error ' + err)
					this.$reportMqttError(err, 'stream.error')
				})
				next.on('close', () => {
					console.error('connect close')
					// 활성 클라이언트가 닫힌 경우에만 재연결(세대 교체로 닫힌 구 연결은 무시)
					if (settled && this.client === next) {
						if (mqttExpiryTimer) {
							clearTimeout(mqttExpiryTimer)
							mqttExpiryTimer = null
						}
						this.reconnectMqtt()
					}
				})
				next.on('disconnect', err  => {
					console.error('disconnected', err)
				})
				next.on('offline', err  => {
					console.error('offline', err)
				})

				let clearDataTimer = null

				next.on('message', async (topic, message, packet) => {
				// QoS 1 중복 수신 방어(msgId 기반)
				if (isDuplicateMessage(message)) {
					console.log('duplicate msgId — skip', topic)
					return
				}

				if (topic.indexOf(clientPrefix) === 0) {
					topic = topic.slice(clientPrefix.length)
				}
				const sep = topic.split('/')

				let data

				if (this.$adminData.training.id) {
					if (this.$adminData.training.id !== parseInt(sep[1])) {
						return
					}
				}

				switch (sep[0]) {
					case 'training':
						switch (sep[2]) {
							case 'stock':
								const stock = JSON.parse(message)
								// msgId는 중복 제거용 메타 필드 — 재고 키가 아니다
								Object.keys(stock).filter(k => k !== 'msgId').forEach(k => {
									this.$set(this.$userData.setting, 'stock_' + k, stock[k])
								})
								break;
							case 'game_start':
							case 'game_reset':
								if (this.training.id && this.training.id !== parseInt(sep[1])) return

								data = JSON.parse(message)
								this.$gameData.mqttData = {
									target: sep[0],
									action: sep[2],
									round: data.round,
									delivery_no: data.delivery_no,
									delivery_start_time1: data.delivery_start_time1,
									delivery_start_time2: data.delivery_start_time2,
								}
								break;
							case 'reload':
								await this.$getUserData()
								break;
							case 'inspection':
							case 'round':
							case 'round_ended':
							case 'round_reset':
								if (this.training.id && this.training.id !== parseInt(sep[1])) return

								data = JSON.parse(message)
								this.$gameData.mqttData = {
									target: sep[0],
									action: sep[2],
									...data
								}
								break;
							case 'ended':
								this.$gameData.mqttData = {
									target: sep[0],
									action: sep[2],
								}
								break;
							case 'update_legos':
								await this.$getUserData()
								this.$gameData.mqttData = {
									target: sep[0],
									action: sep[2],
									...JSON.parse(message),
								}
								break;
						}
						break;
					case 'team':
						switch (sep[3]) {
							case 'inspection':
							case 'direction':
							case 'delivery':
							case 'call':
							case 'call_end':
								this.$gameData.mqttData = {
									target: sep[0],
									action: sep[3],
									...JSON.parse(message),
								}
								break;
							case 'reload':
								await this.$getUserData()
								break;
							case 'game_end':
								this.$gameData.mqttData = {
									target: sep[0],
									action: sep[3],
									...JSON.parse(message),
								}
								break;
						}
						break;
					case 'part':
						switch (sep[4]) {
							case 'reload':
								if (sep[3] === this.user.part) {
									await this.$getUserData()
								}
								break;
							// 관리자 상태 초기화 알림 — 각 파트 화면이 REST 로 다시 읽어 맞춘다.
							// engine 은 part 토픽에서 'direction' 만 처리하므로 이 액션을 무시한다
							// (엔진이 처리하면 캐시의 유령을 보고 상태를 되살릴 수 있다).
							case 'direction_reset':
								this.$gameData.mqttData = {
									target: sep[0],
									action: sep[4],
									...JSON.parse(message),
								}
								break;
							case 'direction':
								// await this.$getUserData()
								this.$gameData.mqttData = {
									target: sep[0],
									action: sep[4],
									...JSON.parse(message),
								}
								break;
						}
						break;
				}

				console.log(topic, message.toString())

				if (clearDataTimer) clearTimeout(clearDataTimer)
				clearDataTimer = setTimeout(() => {
					this.$gameData.mqttData = null
				}, 10)
				})
			} catch (e) {
				console.error('$setMqttClient 오류', e)
				this.$reportMqttError(e, 'setup.error')
				mqttConnecting = false
				this.reconnectMqtt()
			}
		},

		$disconnect() {
			this.client?.end()
		},

		async $publish({type, trainingId, team, part, action, message, qos = 1}) {
			if (!this.client) {
				setTimeout(_ => {
					this.$publish({type, trainingId, team, part, action, message, qos})
				}, 500)
				return
			}

			let topic = `${clientPrefix}${type}/${trainingId}`
			if (team) topic += `/${team}`
			if (part) topic += `/${part}`
			topic += `/${action}`

			// msgId를 객체에 직접 심어 재시도 시에도 같은 ID가 유지되게 한다(수신측 중복 제거의 전제)
			if (message == null) message = {}
			if (typeof message === 'object' && !message.msgId) message.msgId = newMsgId()
			const payload = typeof message === 'object' ? JSON.stringify(message) : message
			try {
				await this.client.publishAsync(topic, payload, { qos: qos })
			} catch (e) {
				console.error('publish error, topic = ' + topic, e)
				this.$reportMqttError(e, 'publish.error')
				this.reconnectMqtt()
				setTimeout(_ => {
					this.$publish({type, trainingId, team, part, action, message, qos})
				}, 500)
			}
		},

		$publishDirection(type, part, direction) {
			const data = {
				id: direction.id,
				type: direction.type,
				order: direction.order,
				part: part,
				team: this.user.team,
				user_id: this.user.id,
				// 열람 주체를 연결 단위로 식별 — engine이 LWT 처리 시 이 값으로 대상을 좁힌다
				clientId: currentClientId,
				gen: currentGeneration,
				sendType: direction.sendType,
				inspected: direction.inspected,
			}
			data[`${part}_status`] = direction[`${part}_status`]

			// 열어둔 지시서를 추적한다 — 발행이 곧 상태 변경이므로 여기서 한 번에 잡는다
			const key = directionKey(data)
			const index = openDirections.findIndex(v => directionKey(v) === key)
			if (isDirectionOpen(part, data)) {
				if (index > -1) openDirections.splice(index, 1, {...data})
				else openDirections.push({...data})
			} else if (index > -1) {
				openDirections.splice(index, 1)
			}

			this.$publishMqtt(type, 'direction', data)
		},

		/**
		 * 지시서 닫기 비컨 — pagehide 시 MQTT 발행은 유실될 수 있다(연결이 이미 죽었거나
		 * 전송 완료 전에 페이지가 파기된다). 브라우저가 전송을 보장하는 sendBeacon 으로
		 * 백엔드에도 닫기를 알린다. 전이(1→0, 3→2)는 서버가 DB 기준으로 판정하므로 멱등이고,
		 * MQTT 와 둘 다 도착해도 무해하다. 엔진 캐시는 reconcile 이 수 초 내 정리한다.
		 */
		$sendCloseBeacon(part, direction) {
			try {
				if (!navigator.sendBeacon || !direction) return
				const token = this.$cookie.get('token')
				if (!token) return
				// 비컨은 CORS preflight 를 수행할 수 없어 application/json 을 못 쓴다 — text/plain 으로 보낸다
				const payload = new Blob([JSON.stringify({
					token: token,
					type: direction.type,
					order: direction.order,
					part: part,
				})], { type: 'text/plain' })
				navigator.sendBeacon(process.env.VUE_APP_API_URL + '/direction/close', payload)
			} catch (e) {
				console.error('지시서 닫기 비컨 전송 실패', e)
			}
		},

		/**
		 * 열어둔 지시서를 모두 닫는다. MQTT 를 정상 종료하기 **전에** 반드시 호출한다.
		 * 발행 완료(QoS 1 PUBACK)를 기다린 뒤 반환하므로, 이후 `end()` 해도 유실되지 않는다.
		 */
		async $closeOpenDirections() {
			if (!openDirections.length) return

			const targets = openDirections.slice()
			openDirections = []

			for (const item of targets) {
				const closed = {...item}
				if (item.part === 'make') {
					closed.make_status = 0
					closed.inspected = 0
				} else {
					// 재발송 열람(3)은 완료(2)로, 최초 열람(1)은 미진행(0)으로 되돌린다
					closed[`${item.part}_status`] = item[`${item.part}_status`] === 3 ? 2 : 0
				}
				// 열림 발행과 같은 msgId 를 그대로 쓰면 수신측 중복 제거에 걸려 조용히 버려진다
				delete closed.msgId

				try {
					await this.$publishMqtt('part', 'direction', closed)
				} catch (e) {
					console.error('열어둔 지시서 닫기 실패', closed, e)
					this.$reportMqttError(e, 'close.direction.error')
				}
			}
		},

		async $publishMqtt(type, action, message, qos = 1) {
			if (!this.client) {
				setTimeout(_ => {
					this.$publishMqtt(type, action, message, qos)
				}, 500)

				return
			}
			const user = this.$userData.user

			if (action === 'direction') {
				switch (user.part) {
					case 'plan':
						delete message.buy_status
						delete message.buy_opened
						delete message.buy_completed
						delete message.make_status
						delete message.make_opened
						delete message.make_completed
						break;
					case 'buy':
						delete message.plan_status
						delete message.plan_opened
						delete message.plan_completed
						delete message.make_status
						delete message.make_opened
						delete message.make_completed
						break;
					case 'make':
						delete message.plan_status
						delete message.plan_opened
						delete message.plan_completed
						delete message.buy_status
						delete message.buy_opened
						delete message.buy_completed
						break;
				}
			}

			let topic = `${clientPrefix}${type}/`
			if (type === 'training') {
				topic += user.training_id || this.training.id
			} else if (type === 'team') {
				topic += `${user.training_id}/${user.team}`
			} else if (type === 'part') {
				topic += `${user.training_id}/${user.team}/${user.part}`
			}
			topic += `/${action}`
			// msgId를 객체에 직접 심어 재시도 시에도 같은 ID가 유지되게 한다(수신측 중복 제거의 전제)
			if (message == null) message = {}
			if (typeof message === 'object' && !message.msgId) message.msgId = newMsgId()
			const payload = typeof message === 'object' ? JSON.stringify(message) : message
			try {
				await this.client.publishAsync(topic, payload, { qos: qos })
			} catch (e) {
				console.error('publish error, topic = ' + topic, e)
				this.$reportMqttError(e, 'publish.error')
				this.reconnectMqtt()
				setTimeout(_ => {
					this.$publishMqtt(type, action, message, qos)
				}, 500)
			}
		},


		async $getUserData() {
			if (!this.user.id) return

			let response
			response = await this.$get('/gamedata')
			this.setting = response.data.setting
			this.teamData = response.data.teamData

			// response = await this.$get('/setting')
			// this.setting = response.data

			// response = await this.$get('/team/data')
			// this.teamData = response.data

			this.$set(this.user, 'capital', this.teamData[`capital_${this.user.part}`])

			// response = await this.$get('/training/data')
			const trainingData = response.data.trainingData
			const stocks = []
			Object.keys(trainingData).forEach(k => {
				if (k.startsWith('stock_')) {
					stocks.push(trainingData[k])
				}
			})
			this.$set(this.$userData, 'data', trainingData)
			this.$set(this.$userData, 'stocks', stocks)

			// response = await this.$get('/inventories')
			this.$set(this.$userData, 'inventories', response.data.inventories)

			if (this.user.part === 'plan') {
			} else if (this.user.part === 'buy') {
				// response = await this.$get('/setting/deliveries')
				this.deliveries = response.data.deliveries
			}
		},

		$showDialog(options) {
			const id = _.uniqueId()
			this.$set(this.$gameData.dialogs, id, {
				dialogId: id,
				...options,
			})

			return id
		},
		$hideDialog(id) {
			if (id) {
				this.$delete(this.$gameData.dialogs, id)
			} else {
				this.$gameData.dialogs = {}
			}
		},
		$showToast(toast) {
			this.$gameData.toast = toast
		}

	}

}
